import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setStarredPeople } from "state/reducers/authReducer";

import {
	Popper,
	Avatar,
	Button,
	Box,
	Drawer,
	Divider,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	ListItemButton,
	ListItemAvatar,
	Collapse,
	Typography,
	useTheme,
	Autocomplete,
	TextField,
	Paper,
	IconButton,
	Tooltip,
	Menu,
	MenuItem,
	Card,
	CardContent,
	CardActionArea,
	Skeleton,
	Chip,
	Toolbar,
	Stack,
    CircularProgress
} from "@mui/material";

import { yellow } from "@mui/material/colors";

import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineOppositeContent, {
	timelineOppositeContentClasses,
} from "@mui/lab/TimelineOppositeContent";

import {
	ExpandLess,
	ExpandMore,
	CalendarMonth,
	MoreHoriz,
	PersonAdd,
	Email,
	PersonAddOutlined,
	StarOutline,
	Star,
} from "@mui/icons-material";
import FlexBetweenBox from "components/FlexBetweenBox";

import { third_party_constants } from "constants/third_party_constants";
import format from "date-fns/format";
import throttle from "lodash/throttle";
import { useNavigate, useParams } from "react-router-dom";
import SearchPeopleAutocomplete from "components/SearchPeopleAutocomplete";
import NoMatchPage from "pages/NoMatchPage";

const PeoplePage = () => {
	const token = useSelector((state) => state.auth.token);
	const starredPeople = useSelector((state) => state.auth.starredPeople);
    const currentWorkspaces = useSelector((state) => state.auth.user?.currentWorkspaces);

	const navigate = useNavigate();
	const dispatch = useDispatch();

	//const currentWorkspace = useSelector((state) => state.auth.user.currentWorkspace);
	const { resourceType, resourceId } = useParams();
	const [person, setPerson] = useState(null);
	const [isStarred, setIsStarred] = useState(false);

	const [interactions, setInteractions] = useState(null);
	const [sharedGroups, setSharedGroups] = useState([
		{ resourceName: "people/c1817117334396051717", groupName: "Team mote" },
		{ resourceName: "contacts/c1817117334396051717", groupName: "CS 98" },
	]);

    const [isLoading, setIsLoading] = useState(false);

	// Fetch User Interactions
	useEffect(() => {}, [person]);

	const [peopleList, setPeopleList] = useState([]);
	// fetch list of all people
	useEffect(() => {
		const getPeople = async () => {
			const url = new URL(`${process.env.REACT_APP_PORT}/people/getPeople`);
			const response = await fetch(url, {
				method: "GET",
				headers: { Authorization: `Bearer ${token}` },
			});
			const data = await response.json();

			if (response.status === 200) {
                console.log('data people', data.people);
				setPeopleList(data.people);
			}
		};
		getPeople();
	}, [currentWorkspaces]);

	// fetch person's info
	useEffect(() => {
		if (!resourceType || !resourceId) return;

		// clear interactions
		setInteractions(null);

		// acquire resource info from url
		const resourceName = `${resourceType}/${resourceId}`;
		const resource = peopleList.find(
			(person) => person.resourceName === resourceName
		);
		if (!resource) return;

		// set person
		const newPerson = {
			name: resource.names?.[0]?.displayName,
			email: resource.emailAddresses?.[0]?.value,
			photo: resource.photos?.[0]?.url,
			resourceName: resource.resourceName,
			workspaceId: resource.workspaceId, // which workspace the person's info is linked to
		};
		setPerson(newPerson);

		// fetch interactions
		const fetchUserInteractions = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_PORT}/people/getUserInteractions/${newPerson.email}/?summarize=true`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                const data = await response.json();
                console.log(data.interactions);

                if (response.status === 200) setInteractions(data.interactions);
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
                setInteractions([]);
            }
		};
		fetchUserInteractions();

		// fetch shared groups and starred status
		const fetchIsStarred = async () => {
			const url = new URL(
				`${process.env.REACT_APP_PORT}/people/isStarred/${newPerson.resourceName}`
			);
			const response = await fetch(url, {
				method: "GET",
				headers: { Authorization: `Bearer ${token}` },
			});
			const data = await response.json();
			console.log(data);

			if (response.status === 200) {
				setIsStarred(data.isStarred);
			}
		};
		fetchIsStarred();
	}, [peopleList, resourceType, resourceId]);

	/* Menu Bar for Contacting */
	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);
	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleStarClick = async () => {
		setIsStarred(!isStarred);

		const url = new URL(
			`${process.env.REACT_APP_PORT}/people/updateStarredPerson`
		);
		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ ...person, newStar: !isStarred }),
		});

		if (response.status === 200) {
			// update starred people list
			const data = await response.json();

			const oldStarredPeople = starredPeople || [];
			const newStarredPeople = data.isStarred
				? [...oldStarredPeople, person]
				: oldStarredPeople?.filter(
						(starredPerson) =>
							starredPerson.resourceName !== person.resourceName
				  );

			dispatch(setStarredPeople({ starredPeople: newStarredPeople }));
		} else {
			// revert to previous state
			setIsStarred(isStarred);
		}
	};

	const handleAddToGroupClick = () => null;

	return (
		person ? (
			<Box width="100%" display="flex" justifyContent="center">
				<Paper
					component={Box}
					m="3rem"
					flexBasis="65%"
					p="2rem"
					gap="1rem"
					display="flex"
					flexDirection="column"
					sx={{ borderRadius: "1rem" }}
					maxHeight="calc(100vh - 64px - 6rem)"
				>
					{/* Header */}
					<FlexBetweenBox justifyContent="center" alignItems="center">
						<Box display="flex" gap="1rem" alignItems="center">
							<Avatar
								alt="Remy Sharp"
								src={person.photo}
								sx={{ width: 50, height: 50 }}
							/>

							<Box>
								<Typography variant="h4">
									{person.name || person.email}
								</Typography>
								<Typography variant="h6" color="text.secondary">
									{person.email}
								</Typography>
							</Box>
						</Box>

						<Box display="flex">
							<Tooltip title="Pin to Side">
								<IconButton disableTouchRipple onClick={handleStarClick}>
									{isStarred ? (
										<Star htmlColor={yellow[600]} />
									) : (
										<StarOutline />
									)}
								</IconButton>
							</Tooltip>

							<Tooltip title="Add to Group">
								<IconButton disableTouchRipple onClick={handleAddToGroupClick}>
									<PersonAddOutlined />
								</IconButton>
							</Tooltip>
						</Box>
					</FlexBetweenBox>

                {/*   sharedGroups.length > 0 && (
                    <Box display="flex" gap="1rem" alignItems="center">
                        <Typography>Shared Groups:</Typography>
                        {sharedGroups.map((group) => (
                            <Tooltip title={group.groupName} key={group.resourceName}>
                                <Chip
                                    label={group.groupName}
                                    variant="contained"
                                    onClick={() => {
                                        //navigate(`/groups/${group.resourceName}`);
                                    }}
                                    disableTouchRipple
                                    sx={{ 
                                        textTransform: 'none',
                                        maxWidth: 100,
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                    }}
                                />
                            </Tooltip>
                        ))}
                    </Box>
                                )*/}

                {  /* Body (Contacting) */ }
                <Box display="flex" gap="1rem">
                    <Button 
                        variant="contained"
                        startIcon={<CalendarMonth />}
                        sx={{ textTransform: 'none' }}
                        onClick={() => navigate(`/calendar/?email=${person.email}&displayName=${person.name}`)}
                    >
                        <Typography>Schedule Meeting</Typography>
                    </Button>
                    {/*
                    <Button 
                        variant="outlined"
                        onClick={handleClick}
                        sx={{ textTransform: 'none' }}
                    >
                        <Typography>Message</Typography>
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        id="account-menu"
                        open={open}
                        onClose={handleClose}
                        onClick={handleClose}
                    >
                        <MenuItem href={`mailto:${person.email}`} >
                            <ListItemIcon>
                                <Email/>
                            </ListItemIcon>
                            Email
                        </MenuItem>
                        <MenuItem onClick={handleClose}>
                            <ListItemIcon>
                                <Email />
                            </ListItemIcon>
                            Slack
                        </MenuItem>
                    </Menu>
                    */}
                </Box>

					<Divider />

					{
						// Body (Interactions)
						interactions ? (
							interactions.length > 0 ? (
								<>
									<Typography variant="h4" fontWeight="600">
										Your Interactions
									</Typography>
									<Box overflow="auto">
										<Timeline
											sx={{
												[`& .${timelineOppositeContentClasses.root}`]: {
													flex: 0.2,
												},
											}}
										>
											{!isLoading && interactions?.map((interaction, i) => (
												<TimelineItem key={interaction.id}>
													<TimelineOppositeContent color="text.secondary">
														{format(
															new Date(interaction.date),
															"MMM dd, yyyy | h:mm a"
														)
															.split("|")
															?.map((line, i) => (
																<Typography
																	key={i}
																	variant="body2"
																	color="text.secondary"
																>
																	{line}
																</Typography>
															))}
													</TimelineOppositeContent>

													<TimelineSeparator>
														<TimelineDot color="background">
															{interaction.type.startsWith("gmail")
																? third_party_constants.gmail.icon()
																: interaction.type === "gcal"
																? third_party_constants.google_calendar.icon()
																: interaction.type === "slack"
																? third_party_constants.slack.icon()
																: undefined}
														</TimelineDot>
														{i !== interactions.length - 1 && (
															<TimelineConnector
																sx={{ bgcolor: "primary.main" }}
															/>
														)}
													</TimelineSeparator>

													<TimelineContent>
														<Typography variant="body"></Typography>
														<Card>
															<CardActionArea
																disableTouchRipple
																href={interaction.htmlLink}
																target="_blank"
															>
																<CardContent>
																	<Typography
																		gutterBottom
																		variant="h5"
																		component="div"
																	>
																		{interaction.title || "(no title)"}
																	</Typography>
																	{interaction.type.startsWith("gmail") && (
																		<Typography
																			variant="body2"
																			color="text.secondary"
																		>
																			{interaction.summary || "(no summary)"}
																		</Typography>
																	)}
																</CardContent>
															</CardActionArea>
														</Card>
													</TimelineContent>
												</TimelineItem>
											))}
                                            {isLoading && <CircularProgress />}
										</Timeline>
									</Box>
								</>
							) : (
								// No interactions found
								<Typography variant="h4">No interactions found</Typography>
							)
						) : (
							<>
								<Typography variant="h4" fontWeight="600">
									Your Interactions
								</Typography>

								{Array(3).map((e, index) => (
									<Box key={index}>
										<Skeleton variant="rectangular" height={300} />
										<Skeleton variant="circular" width={40} height={40} />
										<Skeleton variant="rectangular" height={300} />
									</Box>
								))}
							</>
						)
					}
				</Paper>

				{/*<Paper component={Box} m="3rem" flexDirection="column" flexBasis='25%' p="2rem" sx={{ borderRadius:"1rem"}}>
                <Typography variant="h3">Action Items</Typography>
            </Paper>*/}
			</Box>
		) : (
            <NoMatchPage />
        )
	);
};

export default PeoplePage;
